import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Image} from '@chakra-ui/react'
import {GoogleMap, Marker, InfoWindow} from '@react-google-maps/api'
import {useIntl} from 'react-intl'
import {useGoogleMapsApiLoader} from '../../google-maps-api'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useCountry} from '../../hooks'
import {COUNTRY_MESSAGES} from '../../constants'

export default function MapPage({
    locations,
    activeMarker,
    hideMarkerNumbers,
    selected: selectedProp,
    onSelectedChange
}) {
    const mapRef = useRef(null)
    const intl = useIntl()
    const ocapiSite = useOcapiSite()
    const ocapiSitePreferences = ocapiSite.preferences
    const {googleMapsApiKey} = ocapiSitePreferences

    const isControlled = selectedProp !== undefined && onSelectedChange !== undefined
    const [internalSelected, setInternalSelected] = useState(activeMarker)
    const selected = isControlled ? selectedProp : internalSelected
    const setSelected = isControlled ? onSelectedChange : setInternalSelected
    const [mapsApiLoaded, setMapsApiLoaded] = useState(false)

    const countryCode = useCountry()
    const countryText = intl.formatMessage(COUNTRY_MESSAGES[countryCode])

    const loadApi = useGoogleMapsApiLoader()

    useEffect(() => {
        if (mapsApiLoaded) return
        let unmounted = false
        loadApi().then(() => {
            if (!unmounted) setMapsApiLoaded(true)
        })
        return () => {
            unmounted = true
        }
    }, [loadApi, mapsApiLoaded])

    const boundsToAllMarkers = useCallback(
        (map) => {
            const bounds = new window.google.maps.LatLngBounds()
            locations.forEach((loc) => bounds.extend(loc.position))
            map.fitBounds(bounds)
        },
        [locations]
    )

    const onLoad = useCallback(
        (map) => {
            mapRef.current = map
            if (!activeMarker) {
                boundsToAllMarkers(map)
            }
        },
        [activeMarker, boundsToAllMarkers]
    )

    useEffect(() => {
        if (!mapRef.current) return

        if (activeMarker) {
            mapRef.current.panTo(activeMarker.position)
            mapRef.current.setZoom(16)
            setSelected(activeMarker)
        } else {
            boundsToAllMarkers(mapRef.current)
            setSelected(null)
        }
    }, [activeMarker, boundsToAllMarkers, setSelected])

    return (
        <Box>
            {!mapsApiLoaded || locations.length === 0 ? (
                <Image
                    src={
                        'https://maps.googleapis.com/maps/api/staticmap?center=' +
                        countryText +
                        '&zoom=5&size=500x300&key=' +
                        googleMapsApiKey
                    }
                    width="100%"
                    alt={intl.formatMessage(
                        {
                            defaultMessage: 'Placeholder map for {country}',
                            id: 'map.placeholder_country'
                        },
                        {
                            country: countryText
                        }
                    )}
                />
            ) : (
                <Box
                    role="region"
                    aria-label={intl.formatMessage({
                        defaultMessage: 'Map with all stores',
                        id: 'map.all_stores'
                    })}
                >
                    <GoogleMap
                        mapContainerStyle={{width: '100%', height: '31rem'}}
                        zoom={14}
                        onLoad={onLoad}
                    >
                        {locations.map((loc, index) => (
                            <Marker
                                key={loc.id}
                                position={loc.position}
                                onClick={() => setSelected(loc)}
                                label={
                                    hideMarkerNumbers !== true
                                        ? {
                                              text: String(index + 1),
                                              color: 'white',
                                              fontSize: '16px',
                                              fontWeight: 'bold'
                                          }
                                        : undefined
                                }
                                title={
                                    intl.formatMessage({
                                        defaultMessage: 'Store: ',
                                        id: 'store_locator.store'
                                    }) + loc.name
                                }
                            />
                        ))}

                        {selected?.infoWindowHtml && (
                            <InfoWindow
                                position={selected.position}
                                onCloseClick={() => setSelected(null)}
                            >
                                <Box dangerouslySetInnerHTML={{__html: selected.infoWindowHtml}} />
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </Box>
            )}
        </Box>
    )
}

MapPage.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            position: PropTypes.object.isRequired,
            infoWindowHtml: PropTypes.string
        })
    ).isRequired,
    activeMarker: PropTypes.object,
    hideMarkerNumbers: PropTypes.bool,
    selected: PropTypes.bool,
    onSelectedChange: PropTypes.func
}
